// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE STAFF MESSAGE BOX — DoorlyDesk's binding of the ONE shared composer (components/RichComposer; approved
// 2026-11-02). The unified inbox, the borrower profile's two boxes, the legacy dashboard, both partner panes and the
// ticket reply / internal note all render this, so they cannot drift apart.
//
// What it adds to RichComposer: the staff token, the attachment upload (POST /api/messages/upload-attachment — for a
// staff session the server holds it to the borrower DOCUMENT upload's rules: PDF, JPG, PNG or HEIC, 25 MB, the bytes
// matching the type), and the payload every staff send route reads:
//   { body_html, body, attachment_data?, attachment_name? }
// body_html is sanitized on the server against the one allowlist (backend utils/messageHtml); the preview route and
// the Markdown markers of fp-format are gone (WYSIWYG).
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import RichComposer from '../RichComposer';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
export const STAFF_ATTACH_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic';
export const STAFF_ATTACH_HINT = 'PDF, JPG, PNG or HEIC, up to 25 MB';

/** POST the file to the shared upload route; resolves { data, mime, name } or throws the server's sentence. */
export async function uploadStaffAttachment(token, file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${API}/api/messages/upload-attachment`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.data) throw new Error(d.error || d.message || 'The file could not be attached.');
  return { data: d.data, mime: d.mime, name: d.name || file.name };
}

/**
 * @param {object} p
 * @param {string} p.token                       the staff token (doorly_admin_token)
 * @param {(payload: object) => Promise<boolean>} p.onSend  resolves true when sent (the box then clears)
 * @param {boolean} [p.allowAttachment]          false hides Attach file
 * @param {'reply'|'note'|'message'} [p.mode]    'note' = the violet internal-note box
 * @param {React.ReactNode} [p.topLine]          a quiet line above the box (the ticket's "To: … · Signs as …")
 * @param {string} [p.placeholder] @param {string} [p.sendLabel] @param {string} [p.sendColor] @param {boolean} [p.disabled]
 * @param {string} [p.footerNote] @param {number} [p.rows] @param {boolean} [p.autoFocus] @param {string} [p.testId]
 */
export default function StaffComposer({
  token, onSend, placeholder = `Type a reply… (${/Mac|iPhone|iPad/.test((typeof navigator !== 'undefined' && navigator.platform) || '') ? '⌘' : 'Ctrl+'}↵ to send)`,
  sendLabel = 'Send Reply', allowAttachment = true, disabled = false, footerNote = null, sendColor = '#3a6ad9', rows = 3,
  autoFocus = false, testId = 'staff-composer', mode = 'message', topLine = null,
}) {
  const send = async ({ body_html, body, attachment }) => {
    const payload = { body_html, body };
    if (allowAttachment && attachment) { payload.attachment_data = attachment.data; payload.attachment_name = attachment.name; }
    return onSend(payload);
  };
  return (
    <RichComposer
      onSend={send}
      uploadAttachment={allowAttachment ? (file) => uploadStaffAttachment(token, file) : null}
      attachAccept={STAFF_ATTACH_ACCEPT}
      attachHint={STAFF_ATTACH_HINT}
      mode={mode}
      topLine={topLine}
      placeholder={placeholder}
      sendLabel={sendLabel}
      sendColor={sendColor}
      disabled={disabled}
      autoFocus={autoFocus}
      footerNote={footerNote}
      minLines={Math.max(2, Math.min(rows, 6))}
      maxLines={Math.max(6, rows)}
      fontSize={13}
      formatBarKey="doorly_desk_composer_format_bar"
      testId={testId}
    />
  );
}
